// Types
import type { TListingFilters, TFilterField } from '~/misc/listings/types';
import { ESaleType } from '~/misc/listings/types';

// Utils
import { DateTimeFormatter } from '~/misc/utils/datetime';

/**
 * Wrapped filter payload sent to `POST automated-email/patch-filters`.
 *
 * The backend stores this map loosely (`{ <key>: { value, updated_at } }`) and
 * only reads recognised keys at generation time, so the payload uses the exact
 * keys `SearchParamsBuilder` expects — which differ from the shared listings
 * extractor in two places: `show_stock_for` is included and the frontend
 * `exclude_actioned_properties` is sent as `exclude_actioned`.
 */
export type TWrappedPatchFilters = Record<string, TFilterField<unknown>>;

/**
 * Builds the wrapped patch-filters payload from the modal filter state.
 *
 * Every managed field is sent with a fresh `updated_at` (the backend requires a
 * valid timestamp and there is no per-field conflict resolution for patch
 * filters). The sale `type` (`S`/`L`) is always included (the generator reads it
 * from the stored filters), and `exclude_sold_since` is always sent as enabled
 * because "hide contacts that have moved home" is locked on for automated emails.
 *
 * @param filters - The current modal filter state.
 */
export const buildPatchFiltersPayload = (filters: TListingFilters): TWrappedPatchFilters => {
  const now: string = DateTimeFormatter.toIso8601(new Date());

  const field = <T>(value: T): TFilterField<T> => ({ value, updated_at: now });

  return {
    // Sale direction is always sent; the generator reads it from the stored filters.
    type: field(filters.type ?? ESaleType.SALES),
    price_range: field({
      from: filters.price_range.from ?? null,
      to: filters.price_range.to ?? null,
    }),
    bed_range: field({
      from: filters.bed_range.from ?? null,
      to: filters.bed_range.to ?? null,
    }),
    category: field(filters.category ?? null),
    location: field(filters.location ?? null),
    hidden: field(filters.hidden),
    // Locked on for automated emails — always sent as enabled.
    exclude_sold_since: field(1),
    exclude_archived: field(filters.exclude_archived),
    contact_type: field(filters.contact_type),
    vendor_since: field(filters.vendor_since ?? null),
    landlord_since: field(filters.landlord_since ?? null),
    applicant_since: field(filters.applicant_since ?? null),
    entity_status: field(filters.entity_status),
    show_stock_for: field(filters.show_stock_for),
    // Backend consumes `exclude_actioned`; the frontend key is `exclude_actioned_properties`.
    exclude_actioned: field(filters.exclude_actioned_properties),
    marketing_consent: field(filters.marketing_consent),
  };
};
